/* eslint-disable max-classes-per-file */
import { ApolloLink, execute, makePromise, FetchResult } from 'apollo-link';
import { CustomError } from 'ts-custom-error';
import { onError } from 'apollo-link-error';
import { createUploadLink } from 'apollo-upload-client';
import * as Sentry from '@sentry/browser';
import { DocumentNode, GraphQLError } from 'graphql';
import createDebug from 'debug';
import { ServerError, ServerParseError } from 'apollo-link-http-common';

const debug = createDebug('evp:graphql-client');

export class FetchError extends CustomError {
  public graphQLErrors: readonly GraphQLError[];

  constructor(
    message = 'Could not fetch query',
    errors?: readonly GraphQLError[],
  ) {
    super(message);
    this.graphQLErrors = errors || [];
  }
}

/**
 * Checks if passed err can be cased to a ServerError or ServerParseError
 *
 * In ApolloLink the networkErrors have type `Error | ServerError | ServerParseError`, but
 * it's the `Error` type which has priority in current TypeScript. That means it's hard to
 * get the response data, or statusCode. This function helps with the cast.
 */
function isServerError(err: any): err is ServerError | ServerParseError {
  return !!err.response;
}

function shouldRedirect(): boolean {
  return window.location.href.indexOf('app') > -1;
}

class GraphQLClient {
  link: ApolloLink;

  private path: string;

  private uri: string;

  constructor(path: string) {
    this.path = path;
    this.uri = this.path;
    this.link = this.createLink();
  }

  setURI(uri: string): void {
    this.uri = uri;
    this.link = this.createLink();
  }

  /**
   * Set uri to localhost with a port
   *
   * Useful in tests.
   */
  setLocalhostPort(port: number): void {
    this.uri = `http://localhost:${port}${this.path}`;
    this.link = this.createLink();
  }

  createLink(): ApolloLink {
    return ApolloLink.from([
      onError(({ graphQLErrors, networkError }) => {
        if (graphQLErrors) {
          graphQLErrors.forEach(err => {
            debug(
              '[GraphQL error]: %s\nlocations: %o\npath: %o',
              err.message,
              err.locations,
              err.path,
            );

            Sentry.withScope(scope => {
              scope.setTag('request type', 'graphql');
              scope.setLevel(Sentry.Severity.Error);
              scope.setExtra('locations', err.locations);
              scope.setExtra('path', err.path);
              scope.setExtra('stack', err.stack);
              scope.setExtra('extensions', err.extensions);
              scope.setExtra('nodes', err.nodes);
              scope.setExtra('source', err.source);
              Sentry.captureMessage(err.message);
            });
          });
        }

        if (networkError) {
          debug('[Network error]: %o', networkError);
          if (isServerError(networkError)) {
            // When we get a unauthorized response we redirect the user to /logout, as long as we're
            // in an authenticated route.
            if (networkError.statusCode === 401) {
              if (shouldRedirect()) {
                window.location.replace('/logout');
              }
            }
          }
        }
      }),
      createUploadLink({
        uri: this.uri,
        credentials: 'same-origin',
      }),
    ]);
  }
}

export const client = new GraphQLClient('/api/query');

export async function query<Response>(
  q: DocumentNode,
  variables?: any,
): Promise<Response | undefined> {
  debug('Using deprecated `query` function', variables);

  const res = await makePromise(
    execute(client.link, {
      query: q,
      variables,
    }),
  );

  if (!res.data) {
    return undefined;
  }

  // TODO: Better typecast checking
  return res.data as Response;
}

export interface Response {
  errors?: readonly GraphQLError[];
  data?: {
    [key: string]: any;
  };
}

export async function queryWithError(
  q: DocumentNode,
  variables?: any,
): Promise<Response> {
  debug(
    'Running query against GraphQL backend %O and variables %O...',
    q,
    variables,
  );
  const res = await makePromise<FetchResult>(
    execute(client.link, {
      query: q,
      variables,
    }),
  );

  debug('Got response %O', res);

  const response: Response = {
    errors: res.errors,
    data: res.data || undefined,
  };

  debug('Returning response %O', response);

  return response;
}
